import { ThemeProvider, useTheme } from 'next-themes';
import { useEffect } from 'react';

import { useEmbeddedTheme } from 'src/hooks/useEmbeddedTheme';

import type { FC, ReactNode } from 'react';
import type { EmbeddedTheme } from 'src/hooks/useEmbeddedTheme';

// Kept off the normal `theme` key so an admin previewing a client can't write
// over what that client picked in Settings.
const EMBEDDED_STORAGE_KEY = 'theme-embedded';

/**
 * `forcedTheme` only paints the html class. `resolvedTheme` keeps reporting the
 * stored value, and the components that colour canvases and charts from it would
 * come out of the other scheme — so the provider's own theme is pushed along
 * with it.
 */
const EmbeddedThemeSync: FC<{ theme: EmbeddedTheme }> = ({ theme }) => {
  const { setTheme } = useTheme();

  useEffect(() => {
    setTheme(theme);
  }, [theme, setTheme]);

  return null;
};

/**
 * Light by default. Dark is the newer skin and is not yet at the same finish, so
 * a first visit must not land in it because of the OS setting. `system` stays a
 * choice in Settings, which is why `enableSystem` is on. `next-themes` writes
 * storage only after an explicit choice, so a user keeps the theme they picked.
 *
 * Inside an iframe (Admin impersonation) the embedder picks the theme instead —
 * see `useEmbeddedTheme` for why.
 *
 * Mount this once, at the root. A nested `ThemeProvider` is inert — next-themes
 * renders only its children when a theme context already exists — but a nested
 * *this* is not: the second `useEmbeddedTheme` would start at its light default
 * and its `EmbeddedThemeSync` would push that onto the root context, flipping
 * the app off the embedder's scheme until the next handshake.
 */
export const AppThemeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const embeddedTheme = useEmbeddedTheme();

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme={embeddedTheme ?? 'light'}
      enableSystem={!embeddedTheme}
      forcedTheme={embeddedTheme}
      storageKey={embeddedTheme ? EMBEDDED_STORAGE_KEY : 'theme'}
    >
      {embeddedTheme ? <EmbeddedThemeSync theme={embeddedTheme} /> : null}
      {children}
    </ThemeProvider>
  );
};
